/**
 * PublicLanding — root page for elloraepc.in
 * Visible to all visitors without authentication.
 * Staff click "Staff Login" → /login → post-auth → /app
 * Vendors click "Partner Portal" → /partner (placeholder in P1, full portal in P2)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, HardHat, Layers, Leaf, ChevronRight,
  Phone, Mail, MapPin, ArrowRight,
} from 'lucide-react';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Partners', href: '#partners' },
  { label: 'Contact', href: '#contact' },
];

const SERVICES = [
  {
    icon: Building2,
    title: 'Industrial Construction',
    desc: 'Factory buildings, warehouses, and industrial infrastructure across Maharashtra and neighbouring states.',
  },
  {
    icon: HardHat,
    title: 'Commercial & Institutional',
    desc: 'Corporate offices, hospitals, educational institutions, and mixed-use developments.',
  },
  {
    icon: Layers,
    title: 'Civil Infrastructure',
    desc: 'Roads, bridges, water supply networks, and municipal infrastructure projects.',
  },
  {
    icon: Leaf,
    title: 'Interior Fit-out',
    desc: 'End-to-end interior solutions — from concept to completion for commercial and hospitality spaces.',
  },
];

const PARTNER_POINTS = [
  { num: '01', title: 'Digital RFQs', desc: 'Receive bid requests directly through the partner portal — no phone calls or paper.' },
  { num: '02', title: 'Transparent Payments', desc: 'Track invoice status, payment dates, and outstanding amounts in real time.' },
  { num: '03', title: 'Performance Dashboard', desc: 'See your delivery ratings and quality scores — and improve your standing for future work.' },
  { num: '04', title: 'Compliance Made Easy', desc: 'Upload and manage certificates, licences, and tax documents in one place.' },
];

export default function PublicLanding() {
  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans">
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#3b4872] shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/ellora-logo-blue.png"
              alt="Ellora EPC"
              className="h-9 w-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>

          {/* Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href}
                className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                {l.label}
              </a>
            ))}
          </nav>

          {/* CTAs */}
          <div className="flex items-center gap-3">
            <Link to="/partner"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white border border-white/30 rounded-sm hover:bg-white/10 transition-colors">
              Partner Portal
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-white text-[#3b4872] rounded-sm hover:bg-slate-100 transition-colors"
              data-testid="staff-login-btn">
              Staff Login
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative bg-[#3b4872] overflow-hidden">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-white/80 text-xs font-semibold uppercase tracking-wider mb-6">
              <Building2 size={12} /> 38 Years of Legacy · Nashik, Maharashtra
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
              Building India's<br />
              <span className="text-white">Infrastructural Future</span>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
              Ellora EPC delivers industrial, commercial, and infrastructure construction
              projects with precision engineering, transparent procurement, and on-time delivery.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/partner"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-slate-100 text-[#3b4872] font-bold rounded-sm transition-colors text-sm"
                data-testid="hero-partner-cta">
                Become a Partner <ChevronRight size={16} />
              </Link>
              <a href="#about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white hover:bg-white/10 rounded-sm transition-colors text-sm font-medium">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── About strip ── */}
      <section id="about" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { num: '38+', label: 'Years of Legacy' },
              { num: '500+', label: 'Projects Completed' },
              { num: '50+', label: 'Vendor Partners' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl font-extrabold text-[#3b4872]">{s.num}</div>
                <div className="text-sm text-slate-500 mt-1 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[#3b4872] font-bold text-xs uppercase tracking-widest mb-3">What We Build</div>
            <h2 className="text-3xl font-extrabold text-slate-900">Our Core Services</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title}
                className="bg-slate-50 border border-slate-200 rounded-sm p-6 hover:border-[#3b4872]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-sm bg-[#3b4872]/10 flex items-center justify-center mb-4">
                  <s.icon size={20} className="text-[#3b4872]" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-2">{s.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Partner With Ellora ── */}
      <section id="partners" className="py-20 bg-[#3b4872]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-white/70 font-bold text-xs uppercase tracking-widest mb-3">Vendor Ecosystem</div>
            <h2 className="text-3xl font-extrabold text-white">Why Partner With Ellora</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto text-sm">
              Our digital vendor platform gives suppliers and contractors direct access to
              procurement, transparent payment tracking, and performance data.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {PARTNER_POINTS.map((p) => (
              <div key={p.num} className="bg-white/10 rounded-sm p-6">
                <div className="text-white/50 font-extrabold text-2xl mb-3">{p.num}</div>
                <h3 className="text-white font-bold text-sm mb-2">{p.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/partner"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white hover:bg-slate-100 text-[#3b4872] font-bold rounded-sm transition-colors"
              data-testid="partner-cta-bottom">
              Register as a Partner <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[#3b4872] font-bold text-xs uppercase tracking-widest mb-3">Get In Touch</div>
            <h2 className="text-3xl font-extrabold text-slate-900">Contact Us</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { icon: MapPin, label: 'Address', value: 'Ellora EPC Pvt. Ltd., Nashik, Maharashtra, India' },
              { icon: Phone, label: 'Phone', value: '+91 (253) 000-0000' },
              { icon: Mail, label: 'Email', value: 'info@elloraepc.com' },
            ].map((c) => (
              <div key={c.label} className="text-center">
                <div className="w-10 h-10 rounded-full bg-[#3b4872]/10 flex items-center justify-center mx-auto mb-3">
                  <c.icon size={18} className="text-[#3b4872]" />
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{c.label}</div>
                <div className="text-sm text-slate-700">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 text-white/60 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm">© {new Date().getFullYear()} Ellora EPC Pvt. Ltd. All rights reserved.</div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/login" className="hover:text-white transition-colors">Staff Portal</Link>
            <Link to="/partner" className="hover:text-white transition-colors">Partner Portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
